/**
 * The relay's answers to an invitee as their device reads them, and the bodies it sends (part 38c; part 38a's session
 * routes, tables IS and KW; principle 10, GP1, GP5): each answer read field by field at the boundary (Definition of Done,
 * security), anything else null, which the client counts a failure. Every body leaves from here, so the request
 * inspection has one place to hold to: the claim proof, never the claim secret; a person key only sealed under the
 * keyring key; a keyring key only wrapped under the password.
 */
import type { KdfRecord } from '@cockatrice/crypto';
import type { InviteNotes, InviteRole, KeyGroup } from '@cockatrice/storage';
import { INVITE_NOTES, INVITE_ROLES, KEY_GROUPS } from '@cockatrice/storage';
import { bytesOfLength, fromBase64Url, toBase64Url } from '../account/bytes.ts';
import { kdfJson } from '../account/flows.ts';
import { fieldsOf, readKdf } from '../account/wire.ts';
import { INVITE_WIRE_ID_BYTES, NOTE_PAGE, pulledNoteOf } from '../share/invite-wire.ts';
import type { PulledNote, WireNoteGroup } from '../share/invite-wire.ts';

/** An invite's settings as its invitee is told them (part 38a's `Granted`). */
export interface Granted {
  /** The invite's ID, base64url (16 bytes). */
  readonly invite: string;
  /** Its share's ID, base64url (16 bytes). */
  readonly share: string;
  readonly role: InviteRole;
  readonly notes: InviteNotes;
  readonly export: boolean;
  readonly endsAt: number | null;
  /** The invite's and its share's IDs as bytes (what each place a key or a copy is bound to holds, decision 49). */
  readonly ids: { readonly invite: Uint8Array<ArrayBuffer>; readonly share: Uint8Array<ArrayBuffer> };
}

/** A copy as the claim list names it: its item, version (16 bytes), size and its key wrapped for this invite. */
export interface ListedCopy {
  readonly item: string;
  readonly version: Uint8Array<ArrayBuffer>;
  readonly size: number;
  readonly wrap: Uint8Array<ArrayBuffer>;
}

/** A role key as the claim list hands it: its group, epoch and wrap under the person key. */
export interface ListedRoleKey {
  readonly group: KeyGroup;
  readonly epoch: number;
  readonly wrap: Uint8Array<ArrayBuffer>;
}

/** A claim as `GET /v1/invites` lists it (part 38a's `ListedClaim`). */
export interface ListedClaim extends Granted {
  readonly chapters: readonly string[];
  readonly copies: readonly ListedCopy[];
  readonly roleKeys: readonly ListedRoleKey[];
}

/** The keyring as `GET /v1/keyring` hands it (part 38a's `Keyring`). */
export interface WireKeyring {
  readonly record: { readonly id: Uint8Array<ArrayBuffer>; readonly kdf: KdfRecord; readonly wrapped: Uint8Array<ArrayBuffer> } | null;
  readonly keys: readonly { readonly invite: string; readonly sealed: Uint8Array<ArrayBuffer> }[];
}

/** A page of an invitee's notes (part 38a's `NotePage`). */
export interface InviteeNotes {
  readonly notes: readonly PulledNote[];
  readonly more: boolean;
}

/** A copy's version, as part 38a's `COPY_VERSION_BYTES`. */
export const VERSION_BYTES = 16;

/** The most claims one list holds (part 38a's `MAX_LISTED_CLAIMS`). */
export const MAX_LISTED_CLAIMS = 200;

/** The most items one claim lists: its chapters and its manifest (part 38a's 1 000 chapters a share). */
export const MAX_LISTED_ITEMS = 1001;

/** The most role keys a claim lists: two groups, each at part 38a's `MAX_ROLE_EPOCHS`. */
export const MAX_LISTED_ROLE_KEYS = 128;

/** The most entries a keyring holds: one per claim the account ever made, as far as one list reads (bounded by the relay). */
export const MAX_KEYRING_KEYS = 10_000;

/** The most bytes a wrap or a sealed entry is read at (part 38a's `MAX_WRAP_BYTES`, `MAX_SEALED_KEY_BYTES`). */
export const MAX_WRAP_BYTES = 256;

/** An item's name: 1 to 64 of base64url's alphabet (part 38a's `chapterOf`). */
const ITEM = /^[A-Za-z0-9_-]{1,64}$/u;

function itemOf(value: unknown): string | null {
  return typeof value === 'string' && ITEM.test(value) ? value : null;
}

function idOf(value: unknown): string | null {
  return typeof value === 'string' && bytesOfLength(value, INVITE_WIRE_ID_BYTES) !== null ? value : null;
}

function countOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function epochOf(value: unknown): number | null {
  const count = countOf(value);
  return count !== null && count < 2 ** 31 ? count : null;
}

/** Base64url bytes, 1 to `max` long; null otherwise. */
function someBytes(value: unknown, max: number): Uint8Array<ArrayBuffer> | null {
  if (typeof value !== 'string') return null;
  const bytes = fromBase64Url(value);
  return bytes === null || bytes.length === 0 || bytes.length > max ? null : bytes;
}

/** A plain array of at most `max` entries, each read by `read`; null if any isn't one. */
function listOf<T>(value: unknown, max: number, read: (entry: unknown) => T | null): T[] | null {
  if (!Array.isArray(value) || value.length > max) return null;
  const read_ = value.map(read);
  return read_.every((entry): entry is T => entry !== null) ? read_ : null;
}

/** An invite's settings (the claim's answer and each listed claim), or null. */
function grantedOf(fields: Readonly<Record<string, unknown>> | null): Granted | null {
  const invite = bytesOfLength(fields?.['invite'], INVITE_WIRE_ID_BYTES);
  const share = bytesOfLength(fields?.['share'], INVITE_WIRE_ID_BYTES);
  const role = INVITE_ROLES.find((known) => known === fields?.['role']) ?? null;
  const notes = INVITE_NOTES.find((known) => known === fields?.['notes']) ?? null;
  const exported = fields?.['export'];
  const raw = fields?.['endsAt'];
  const endsAt = raw === null ? null : countOf(raw);
  if (invite === null || share === null || role === null || notes === null || typeof exported !== 'boolean' || (raw !== null && endsAt === null)) return null;
  // Each ID in its one spelling (the relay's), kept with its bytes.
  return { invite: toBase64Url(invite), share: toBase64Url(share), role, notes, export: exported, endsAt, ids: { invite, share } };
}

/** `POST /v1/invites/claim`: the invite's settings (`claimed` or `done`), or null. */
export function readClaimed(value: unknown): Granted | null {
  return grantedOf(fieldsOf(value));
}

function copyOf(value: unknown): ListedCopy | null {
  const fields = fieldsOf(value);
  const item = itemOf(fields?.['chapter']);
  const version = bytesOfLength(fields?.['version'], VERSION_BYTES);
  const size = countOf(fields?.['size']);
  const wrap = someBytes(fields?.['wrap'], MAX_WRAP_BYTES);
  return item === null || version === null || size === null || wrap === null ? null : { item, version, size, wrap };
}

function roleKeyOf(value: unknown): ListedRoleKey | null {
  const fields = fieldsOf(value);
  const group = KEY_GROUPS.find((known) => known === fields?.['group']) ?? null;
  const epoch = epochOf(fields?.['epoch']);
  const wrap = someBytes(fields?.['wrap'], MAX_WRAP_BYTES);
  return group === null || epoch === null || wrap === null ? null : { group, epoch, wrap };
}

function claimOf(value: unknown): ListedClaim | null {
  const fields = fieldsOf(value);
  const granted = grantedOf(fields);
  const chapters = listOf(fields?.['chapters'], MAX_LISTED_ITEMS, itemOf);
  const copies = listOf(fields?.['copies'], MAX_LISTED_ITEMS, copyOf);
  const roleKeys = listOf(fields?.['roleKeys'], MAX_LISTED_ROLE_KEYS, roleKeyOf);
  return granted === null || chapters === null || copies === null || roleKeys === null ? null : { ...granted, chapters, copies, roleKeys };
}

/** `GET /v1/invites`: `{ invites }`, each claim read whole (one malformed claim makes the list a failure, GP5), or null. */
export function readClaims(value: unknown): readonly ListedClaim[] | null {
  return listOf(fieldsOf(value)?.['invites'], MAX_LISTED_CLAIMS, claimOf);
}

function keyOf(value: unknown): WireKeyring['keys'][number] | null {
  const fields = fieldsOf(value);
  const invite = idOf(fields?.['invite']);
  const sealed = someBytes(fields?.['sealed'], MAX_WRAP_BYTES);
  return invite === null || sealed === null ? null : { invite, sealed };
}

/** `GET /v1/keyring`: `{ record, keys }`, or null. */
export function readKeyring(value: unknown): WireKeyring | null {
  const fields = fieldsOf(value);
  const raw = fields?.['record'];
  const keys = listOf(fields?.['keys'], MAX_KEYRING_KEYS, keyOf);
  if (keys === null || raw === undefined) return null;
  if (raw === null) return { record: null, keys };
  const record = fieldsOf(raw);
  const id = bytesOfLength(record?.['id'], 16);
  const kdf = readKdf({ kdf: record?.['kdf'] });
  const wrapped = someBytes(record?.['wrapped'], MAX_WRAP_BYTES);
  return id === null || kdf === null || wrapped === null ? null : { record: { id, kdf, wrapped }, keys };
}

/** `POST /v1/invites/notes`: `{ notes, more }`, or null. */
export function readInviteeNotes(value: unknown): InviteeNotes | null {
  const fields = fieldsOf(value);
  const notes = listOf(fields?.['notes'], NOTE_PAGE, pulledNoteOf);
  const more = fields?.['more'];
  return notes === null || typeof more !== 'boolean' ? null : { notes, more };
}

/** `POST /v1/invites/claim`'s body: the invite and its claim proof (GP1: never the claim secret). */
export function claimBody(invite: Uint8Array, proof: Uint8Array): Record<string, unknown> {
  return { invite: toBase64Url(invite), claim: toBase64Url(proof) };
}

/** `POST /v1/keyring`'s body (KW1, CL1): the keyring's ID, its wrap, the KDF record it is under, the grant, and the first entry. */
export function keyringBody(
  id: Uint8Array,
  wrapped: Uint8Array,
  kdf: KdfRecord,
  grant: string,
  key: { readonly invite: string; readonly sealed: Uint8Array } | null,
): Record<string, unknown> {
  return { id: toBase64Url(id), wrapped: toBase64Url(wrapped), kdf: kdfJson(kdf), grant, ...(key === null ? {} : { key: { invite: key.invite, sealed: toBase64Url(key.sealed) } }) };
}

/** `POST /v1/keyring/key`'s body: an entry (a person key sealed under the keyring key) and the grant. */
export function keyBody(invite: string, sealed: Uint8Array, grant: string): Record<string, unknown> {
  return { invite, sealed: toBase64Url(sealed), grant };
}

/** `POST /v1/invites/copy`'s body: one item of a claim. */
export function copyBody(invite: string, item: string): Record<string, unknown> {
  return { invite, chapter: item };
}

/** `POST /v1/invites/notes`'s body: the notes changed after `after`. */
export function notesBody(invite: string, after: number): Record<string, unknown> {
  return { invite, after };
}

/** `POST /v1/invites/note`'s body (NV): the note's ID, group, the key's epoch (0 for `person`) and its sealed body. */
export function noteBody(invite: string, id: Uint8Array, group: WireNoteGroup, epoch: number, body: Uint8Array): Record<string, unknown> {
  return { invite, id: toBase64Url(id), group, epoch, body: toBase64Url(body) };
}

/** `POST /v1/invites/note/delete`'s body: one of the invitee's own notes. */
export function noteRefBody(invite: string, id: Uint8Array): Record<string, unknown> {
  return { invite, id: toBase64Url(id) };
}
